import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface Organization {
  id: string;
  name: string;
}

interface OrganizationMultiSelectProps {
  selectedOrganizations: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  disabled?: boolean;
}

export const OrganizationMultiSelect: React.FC<OrganizationMultiSelectProps> = ({
  selectedOrganizations,
  onSelectionChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');

      if (error) {
        throw error;
      }

      setOrganizations(data || []);
    } catch (error: any) {
      console.error('Error fetching organizations:', error);
      toast({
        title: t('orgSelect.error.fetch', 'Failed to load organizations'),
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (organizationId: string) => {
    const isSelected = selectedOrganizations.includes(organizationId);
    let newSelection;
    
    if (isSelected) {
      newSelection = selectedOrganizations.filter(id => id !== organizationId);
    } else {
      newSelection = [...selectedOrganizations, organizationId];
    }
    
    onSelectionChange(newSelection);
  };

  const handleRemove = (organizationId: string) => {
    const newSelection = selectedOrganizations.filter(id => id !== organizationId);
    onSelectionChange(newSelection);
  };

  const getSelectedOrganizationNames = () => {
    return organizations
      .filter(org => selectedOrganizations.includes(org.id))
      .map(org => org.name);
  };

  const getDisplayText = () => {
    const selectedCount = selectedOrganizations.length;
    const totalCount = organizations.length;
    
    if (selectedCount === 0) {
      return t('orgSelect.placeholder', 'Select organizations...');
    }
    
    if (selectedCount === totalCount) {
      return t('orgSelect.allSelected', 'All organizations');
    }
    
    if (selectedCount === 1) {
      const selectedOrg = organizations.find(org => org.id === selectedOrganizations[0]);
      return selectedOrg?.name || t('orgSelect.oneSelected', '1 organization');
    }
    
    return t('orgSelect.multipleSelected', `${selectedCount} organizations`);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between"
            disabled={disabled || loading}
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder={t('orgSelect.search', 'Search organizations...')} 
            />
            <CommandEmpty>
              {t('orgSelect.noResults', 'No organizations found.')}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {organizations.map((organization) => (
                <CommandItem
                  key={organization.id}
                  onSelect={() => handleSelect(organization.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedOrganizations.includes(organization.id)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {organization.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      
      {/* Selected Organizations Display */}
      {selectedOrganizations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {getSelectedOrganizationNames().map((name, index) => (
            <Badge
              key={selectedOrganizations[index]}
              variant="secondary"
              className="pr-1"
            >
              {name}
              <button
                type="button"
                className="ml-1 hover:bg-muted rounded-sm p-0.5"
                onClick={() => handleRemove(selectedOrganizations[index])}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
